import React from 'react';
import { Brain, Crosshair, ShieldCheck, Wallet, ListChecks, Activity } from 'lucide-react';
import WorldMap from '@/components/marketing/WorldMap';
import DecorBackground from '@/components/marketing/DecorBackground';
import Reveal from '@/components/marketing/Reveal';

const ITEMS = [
  { icon: Brain, accent: '#8B5CF6', title: 'Predictive Source Scoring', text: 'Evaluate source potential before scaling traffic.' },
  { icon: Crosshair, accent: '#22D3EE', title: 'Smart GEO & Device Routing', text: 'Match offers with GEO, device, and format combinations.' },
  { icon: ShieldCheck, accent: '#34D399', title: 'Fraud Signal Detection', text: 'Filter suspicious patterns before they waste budget.' },
  { icon: Wallet, accent: '#FBBF24', title: 'Budget Flow Suggestions', text: 'Guide top-up allocation based on campaign requirements.' },
  { icon: ListChecks, accent: '#3B82F6', title: 'Whitelist / Blacklist Insights', text: 'Build cleaner traffic paths using source-level controls.' },
  { icon: Activity, accent: '#22D3EE', title: 'Performance Pattern Monitoring', text: 'Track signals that help optimize campaign direction.' },
];

const FLOAT = [
  { label: 'Smart GEO Routing', tone: '#22D3EE' },
  { label: 'Source Quality 94.6', tone: '#34D399' },
  { label: 'Fraud Signals Checked', tone: '#34D399' },
  { label: 'AI Bid Suggestions', tone: '#8B5CF6' },
  { label: 'Live Traffic Flow', tone: '#3B82F6' },
];

export default function AISection() {
  return (
    <section className="relative bg-[#08111F] text-white isolate overflow-hidden" data-testid="home-ai-section">
      <DecorBackground variant="section" />
      {/* Extra violet/blue glow */}
      <div
        className="absolute inset-0 -z-10 pointer-events-none"
        aria-hidden
        style={{ background: 'radial-gradient(700px 400px at 25% 35%, rgba(139,92,246,0.16), transparent 60%), radial-gradient(700px 400px at 80% 65%, rgba(59,130,246,0.16), transparent 60%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-[#7DDCFF]/20 text-[12px] font-medium text-[#22D3EE] backdrop-blur-md">
              <span className="h-1.5 w-1.5 rounded-full bg-[#8B5CF6]" /> Intelligence Layer
            </span>
            <h2 className="font-display-tight mt-5 text-[36px] sm:text-[48px] font-bold tracking-[-0.035em] leading-[1.05]">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#22D3EE] via-[#3B82F6] to-[#8B5CF6]">AI-Powered</span>{' '}Traffic Intelligence
            </h2>
            <p className="mt-4 text-[17px] text-slate-300/85 leading-[1.65]">
              Use smarter routing signals, source-level insights, and campaign-fit analysis to launch traffic with more confidence.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
          {/* Left: world map */}
          <Reveal className="lg:col-span-6" delay={0.05}>
            <div className="relative rounded-3xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-[#7DDCFF]/20 backdrop-blur-xl overflow-hidden p-4 sm:p-6 min-h-[420px] shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
              <div className="absolute top-3 left-4 flex items-center gap-2 z-10">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B172A]/80 border border-white/10 text-[10.5px] uppercase tracking-[0.18em] text-slate-300 backdrop-blur-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-glow" /> Global Traffic
                </span>
              </div>
              <WorldMap />
              {/* Floating labels */}
              <div className="absolute right-4 top-4 sm:right-6 sm:top-6 grid gap-2 max-w-[60%]">
                {FLOAT.map((f, i) => (
                  <div
                    key={f.label}
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0B172A]/85 border border-white/10 backdrop-blur-md text-[11px] font-medium text-slate-200 float-y"
                    style={{ animationDelay: `${i * 0.6}s` }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: f.tone, boxShadow: `0 0 10px ${f.tone}` }} />
                    {f.label}
                  </div>
                ))}
              </div>
              <div
                className="pointer-events-none absolute inset-0"
                aria-hidden
                style={{ background: 'radial-gradient(420px 280px at 70% 50%, rgba(34,211,238,0.10), transparent 70%)' }}
              />
            </div>
          </Reveal>

          {/* Right: feature cards */}
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ITEMS.map((it, i) => {
              const Icon = it.icon;
              return (
                <Reveal key={it.title} delay={0.05 + i * 0.05}>
                  <div
                    data-testid="ai-feature-card"
                    className="group relative rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 p-5 backdrop-blur-xl lift-on-hover overflow-hidden h-full"
                  >
                    <div
                      className="absolute top-0 left-0 right-0 h-px opacity-70 group-hover:opacity-100 transition-opacity"
                      style={{ background: `linear-gradient(90deg, transparent, ${it.accent}, transparent)` }}
                      aria-hidden
                    />
                    <div
                      className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: `radial-gradient(circle, ${it.accent}33, transparent 65%)` }}
                      aria-hidden
                    />
                    <span
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                      style={{
                        background: `linear-gradient(135deg, ${it.accent}26, ${it.accent}10)`,
                        borderColor: `${it.accent}40`,
                        color: it.accent,
                        boxShadow: `0 0 22px ${it.accent}33`,
                      }}
                    >
                      <Icon className="h-4.5 w-4.5" />
                    </span>
                    <h3 className="font-display mt-4 text-[16.5px] font-semibold tracking-[-0.02em] text-white">{it.title}</h3>
                    <p className="mt-2 text-[13.5px] text-slate-300/85 leading-relaxed">{it.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <p className="mt-10 text-[12px] text-slate-500">
          We do not claim guaranteed ROI or fully automated AI optimization. Smart routing combines tooling and human review.
        </p>
      </div>
    </section>
  );
}
